import type { ReferenceCapture, ReferenceCaptureInput } from '../data/contracts';
import { browserEncodeEnv, type EncodeEnv, sniffAdmittedType } from '../presentation/assetEncoding';
import { validTourSelector } from './tourPlan';

/** Octets de démonstration uniquement en mémoire, jamais dans l'état sérialisé du dépôt. */
/** Fond peint sous une image avant l'encodage JPEG (sans transparence) : un fond d'image, pas une couleur d'interface — aucun jeton de thème ne s'applique. */
const JPEG_MATTE = 'white';
export class CaptureFixture {
  private rows = new Map<string, Map<string, { metadata: ReferenceCapture; content: ArrayBuffer; thumb: ArrayBuffer }>>();
  constructor(private env: EncodeEnv = browserEncodeEnv) {}

  clear(dossier?: string): void { if (dossier) this.rows.delete(dossier); else this.rows.clear(); }
  list(dossier: string): ReferenceCapture[] { return [...(this.rows.get(dossier)?.values() ?? [])].map(row => ({ ...row.metadata, usedBy: [...row.metadata.usedBy] })); }
  bytes(dossier: string, hash: string, thumb: boolean): ArrayBuffer {
    const row = this.rows.get(dossier)?.get(hash);
    if (!row) throw new Error('capture.not_found');
    return (thumb ? row.thumb : row.content).slice(0);
  }
  delete(dossier: string, hash: string, usedBy: number[]): void {
    if (!this.rows.get(dossier)?.has(hash)) throw new Error('capture.not_found');
    if (usedBy.length) throw new Error('capture.in_use');
    this.rows.get(dossier)?.delete(hash);
  }
  async upload(dossier: string, image: Blob, input: ReferenceCaptureInput, actor: string): Promise<ReferenceCapture> {
    if (!input.rightsAttested) throw new Error('capture.rights_required');
    if (!input.label.trim() || input.label.length > 120 || (input.selector && !validTourSelector(input.selector))) throw new Error('capture.metadata_invalid');
    if (image.size > 614400) throw new Error('capture.too_large');
    const signature = sniffAdmittedType(new Uint8Array(await image.slice(0, 16).arrayBuffer()));
    if (signature !== 'image/png' && signature !== 'image/jpeg') throw new Error('capture.type');
    const decoded = await this.env.decode(image);
    try {
      if (decoded.width < 1 || decoded.height < 1 || Math.max(decoded.width, decoded.height) > 1600) throw new Error('capture.dimensions');
      const encode = async (scale: number): Promise<Blob> => {
        const canvas = this.env.createCanvas(Math.max(1, Math.round(decoded.width * scale)), Math.max(1, Math.round(decoded.height * scale)));
        const context = canvas.getContext('2d');
        if (!context) throw new Error('capture.tainted');
        context.fillStyle = JPEG_MATTE; context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);
        return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('capture.tainted')), 'image/jpeg', 0.82));
      };
      const original = await encode(1);
      if (original.size > 614400) throw new Error('capture.too_large');
      const content = await original.arrayBuffer();
      const hash = [...new Uint8Array(await crypto.subtle.digest('SHA-256', content))].map(byte => byte.toString(16).padStart(2, '0')).join('');
      let scale = Math.min(1, 480 / Math.max(decoded.width, decoded.height));
      let thumbnail = await encode(scale);
      while (thumbnail.size > 65536 && scale > 0.02) { scale *= 0.75; thumbnail = await encode(scale); }
      if (thumbnail.size > 65536) throw new Error('capture.too_large');
      const thumb = await thumbnail.arrayBuffer();
      const rows = this.rows.get(dossier) ?? new Map();
      const duplicate = rows.get(hash);
      if (duplicate) return { ...duplicate.metadata, usedBy: [...duplicate.metadata.usedBy] };
      if (rows.size >= 120 || [...rows.values()].reduce((total, row) => total + row.metadata.bytes, original.size) > 24 * 1024 * 1024) throw new Error('capture.limit');
      const now = new Date().toISOString();
      const doc = new DOMParser().parseFromString(input.label, 'text/html');
      doc.querySelectorAll('script,style').forEach(node => node.remove());
      const metadata: ReferenceCapture = { sha256: hash, mediaType: 'image/jpeg', width: decoded.width, height: decoded.height, bytes: original.size,
        importId: input.importId, pieceId: input.pieceId, selector: input.selector ?? null, label: (doc.body.textContent ?? '').trim().slice(0, 120),
        thumbWidth: Math.max(1, Math.round(decoded.width * scale)), rightsAttestedBy: actor, rightsAttestedAt: now, createdAt: now, usedBy: [] };
      rows.set(hash, { metadata, content, thumb }); this.rows.set(dossier, rows);
      return { ...metadata, usedBy: [] };
    } finally { decoded.close?.(); }
  }
}
